import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Send, User, Clock, CheckCircle2, HelpCircle, MessageCircleQuestion,
  CalendarDays, RotateCcw,
} from 'lucide-react';
import BackButton from '../components/BackButton';
import {
  getQuestionById,
  getQuestionMessages,
  addQuestionMessage,
  subscribeToQuestionMessages,
  updateQuestion
} from '../services/firebase/questions';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../contexts/ResponsiveContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { formatTimeAgo } from '../utils/timeAgo';
import PortalBackground from '../components/PortalBackground';

const STATUS_META = {
  Open: {
    label: 'Open',
    icon: HelpCircle,
    badge: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  },
  'In Progress': {
    label: 'In Progress',
    icon: Clock,
    badge: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  },
  Resolved: {
    label: 'Resolved',
    icon: CheckCircle2,
    badge: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
  },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.Open;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${meta.badge}`}>
      <Icon className="w-3.5 h-3.5" />
      {meta.label}
    </span>
  );
};

const PortalQuestionDetail = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isMobile } = useResponsive();

  // State
  const [question, setQuestion] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const messagesEndRef = useRef(null);

  // Load question and messages on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Load question details
        const questionData = await getQuestionById(questionId);
        setQuestion(questionData);

        // Load messages
        const messagesData = await getQuestionMessages(questionId);
        setMessages(messagesData);

        setLoading(false);
      } catch (error) {
        console.error('Error loading question data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, [questionId]);

  // Load current user profile
  useEffect(() => {
    const loadCurrentUserProfile = async () => {
      if (!currentUser) return;

      try {
        // Try to get from users collection (portal users)
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const profileImage = userData.profileImage || currentUser.photoURL;

          setCurrentUserProfile({
            id: currentUser.uid,
            name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.displayName || currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
            profileImage: profileImage,
            role: userData.role || 'client'
          });
          return;
        }

        // Fallback to basic user info
        setCurrentUserProfile({
          id: currentUser.uid,
          name: currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
          profileImage: currentUser.photoURL,
          role: 'user'
        });
      } catch (error) {
        console.error('Error loading current user profile:', error);
        // Fallback to basic user info
        setCurrentUserProfile({
          id: currentUser.uid,
          name: currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
          profileImage: currentUser.photoURL,
          role: 'user'
        });
      }
    };

    loadCurrentUserProfile();
  }, [currentUser]);

  // Function to check if image URL is accessible
  const isImageAccessible = (url) => {
    if (!url) return false;

    // Google profile images often have CORS issues, so we'll show fallback for them
    if (url.includes('googleusercontent.com') || url.includes('lh3.googleusercontent.com')) {
      return false;
    }

    return true;
  };

  // Set up real-time listener for messages
  useEffect(() => {
    if (questionId) {
      const unsubscribe = subscribeToQuestionMessages(questionId, (messagesData) => {
        setMessages(messagesData);
      });

      return () => unsubscribe();
    }
  }, [questionId]);

  // Keep the conversation scrolled to the latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages]);

  // Handle sending message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !currentUserProfile || sendingMessage) return;

    try {
      setSendingMessage(true);
      await addQuestionMessage(questionId, {
        senderId: currentUserProfile.id,
        senderName: currentUserProfile.name,
        senderRole: currentUserProfile.role,
        messageText: newMessage.trim(),
        attachments: []
      });

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  // Enter sends, Shift+Enter adds a newline
  const handleComposerKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle status changes (resolve / reopen)
  const handleStatusChange = async (newStatus) => {
    if (!question || question.status === newStatus || updatingStatus) return;

    try {
      setUpdatingStatus(true);
      const updateData = newStatus === 'Resolved'
        ? { status: 'Resolved', resolvedAt: new Date() }
        : { status: newStatus, resolvedAt: null };
      await updateQuestion(questionId, updateData);
      setQuestion(prev => ({ ...prev, ...updateData }));
    } catch (error) {
      console.error('Error updating question status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="relative container mx-auto px-4 py-6 min-h-screen">
        <PortalBackground />
        <div className="relative grid grid-cols-1 lg:grid-cols-3 gap-6 mt-16">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 animate-pulse space-y-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-3 rounded bg-gray-100 dark:bg-gray-800" style={{ width: `${80 - i * 12}%` }}></div>
            ))}
          </div>
          <div className="lg:col-span-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 animate-pulse space-y-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-3 rounded bg-gray-100 dark:bg-gray-800" style={{ width: `${90 - i * 10}%` }}></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!question) {
    return (
      <div className="relative container mx-auto px-4 py-6 min-h-screen">
        <PortalBackground />
        <div className="relative rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm flex flex-col items-center justify-center py-16 px-6 text-center mt-10">
          <MessageCircleQuestion className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Question not found</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">It may have been removed, or the link is incorrect.</p>
          <button
            onClick={() => navigate('/portal/my-questions')}
            className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-colors"
          >
            Back to My Questions
          </button>
        </div>
      </div>
    );
  }

  const isResolved = question.status === 'Resolved';

  return (
    <div className={`relative container mx-auto ${isMobile ? 'px-3 py-4' : 'px-4 py-6'} min-h-screen`}>
      <PortalBackground />
      <div className="relative">
      {/* Header */}
      <div className={`relative ${isMobile ? 'mb-4' : 'mb-6'}`}>
        <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10">
          <BackButton onClick={() => navigate('/portal/my-questions')} />
        </div>
        <div className={`text-center ${isMobile ? 'px-14' : 'px-32'} py-1`}>
          <h1 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold tracking-tight text-gray-900 dark:text-white truncate`}>Question Details</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Asked {formatTimeAgo(question.createdAt)} · {question.assigneeName || 'Unassigned'}
          </p>
        </div>
        <div className="absolute right-0 top-1/2 -translate-y-1/2 z-10">
          <StatusBadge status={question.status} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Question Details */}
        <div className="lg:col-span-1">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-5 lg:sticky lg:top-6">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-2 mb-4 text-center">
              <MessageCircleQuestion className="w-4 h-4 text-red-600" />
              Question Info
            </h2>

            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 flex items-center justify-center shrink-0">
                  <Clock className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</p>
                  <div className="mt-0.5"><StatusBadge status={question.status} /></div>
                </div>
              </div>

              {/* Assigned To */}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 flex items-center justify-center shrink-0">
                  <User className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Assigned To</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {question.assigneeName || 'Unassigned'}
                  </p>
                </div>
              </div>

              {/* Created Date */}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 flex items-center justify-center shrink-0">
                  <CalendarDays className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Created</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {question.createdAt ? new Date(question.createdAt).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    }) : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Resolved Date */}
              {question.resolvedAt && (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Resolved</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {new Date(question.resolvedAt).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              )}

              {/* Status actions */}
              <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
                {isResolved ? (
                  <button
                    onClick={() => handleStatusChange('Open')}
                    disabled={updatingStatus}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-60 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    {updatingStatus ? 'Reopening...' : 'Reopen Question'}
                  </button>
                ) : (
                  <button
                    onClick={() => handleStatusChange('Resolved')}
                    disabled={updatingStatus}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 transition-colors"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {updatingStatus ? 'Updating...' : 'Mark as Resolved'}
                  </button>
                )}
                {!isResolved && (
                  <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 text-center">
                    Got your answer? Mark this resolved to close it out.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Conversation */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm flex flex-col">
            {/* Original question */}
            <div className="p-5 border-b border-gray-100 dark:border-gray-800">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5 text-center">Your Question</p>
              <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">{question.questionText}</p>
            </div>

            {/* Messages List */}
            <div className="p-5 flex-1 overflow-y-auto" style={{ maxHeight: '28rem' }}>
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <Send className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-2" />
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No replies yet</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Our team will respond here — feel free to add more detail below.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map(message => {
                    const isCurrentUser = message.senderId === currentUserProfile?.id;
                    return (
                      <div key={message.id} className={`flex gap-2.5 ${isCurrentUser ? 'flex-row-reverse' : ''}`}>
                        <div className="flex-shrink-0">
                          {isCurrentUser ? (
                            <div className="relative">
                              {currentUserProfile?.profileImage && isImageAccessible(currentUserProfile.profileImage) ? (
                                <img
                                  src={currentUserProfile.profileImage}
                                  alt={currentUserProfile.name}
                                  className="w-8 h-8 rounded-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div
                                className={`w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white text-sm font-medium ${
                                  currentUserProfile?.profileImage && isImageAccessible(currentUserProfile.profileImage) ? 'hidden' : 'flex'
                                }`}
                              >
                                {currentUserProfile?.name?.charAt(0) || 'U'}
                              </div>
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                              {message.senderName?.charAt(0) || 'U'}
                            </div>
                          )}
                        </div>
                        <div className={`max-w-[80%] ${isCurrentUser ? 'text-right' : ''}`}>
                          <div className={`flex items-baseline gap-2 mb-1 ${isCurrentUser ? 'justify-end' : ''}`}>
                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                              {isCurrentUser ? 'You' : message.senderName}
                            </span>
                            <span className="text-[11px] text-gray-400 dark:text-gray-500">
                              {formatTimeAgo(message.timestamp)}
                            </span>
                          </div>
                          <div className={`inline-block px-3.5 py-2.5 text-sm leading-relaxed text-left ${
                            isCurrentUser
                              ? 'bg-red-600 text-white rounded-2xl rounded-tr-sm'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-2xl rounded-tl-sm'
                          }`}>
                            <p className="whitespace-pre-wrap">{message.messageText}</p>
                            {message.attachments && message.attachments.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs opacity-75">Attachments:</p>
                                <ul className="text-xs">
                                  {message.attachments.map((attachment, idx) => (
                                    <li key={idx}>{attachment.name}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="p-4 border-t border-gray-100 dark:border-gray-800">
              <form onSubmit={handleSendMessage} className="flex items-end gap-2">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleComposerKeyDown}
                  placeholder="Type a reply... (Enter to send)"
                  className="flex-1 px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  rows="2"
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim() || sendingMessage}
                  className="p-3 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white rounded-xl transition-colors shrink-0"
                  title="Send message"
                >
                  {sendingMessage ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default PortalQuestionDetail;
